import { useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { format, isValid, parse } from "date-fns";
// import { PropsValue } from "react-select";

import DatePicker from "./DatePicker";
import Input from "./Input";
import Select from "./Select";
import SelectWithSearch, { SelectOption } from "./SelectWithSearch";
import Tabs from "./Tabs";
import { FilterInput } from "src/types/report";
import { END_DATE, START_DATE } from "src/constants/filter";
import { getInitialValues } from "src/helpers/Filter";

type ReportFiltersProps = {
  inputFields?: FilterInput[];
  defaultValues?: Record<string, string | number>;
  onSubmit?: (filters: { fields: Record<string, string | number> }) => void;
  onClear?: () => void;
  autoSubmitOnTabChange?: boolean;
  isLoading?: boolean;
  showLabels?: boolean;
};

// type StaticSelectOption = {
//   label: string;
//   value: string | number;
// };

const toValidDate = (
  value: string | number | undefined,
  fallback: string,
): Date | null => {
  const parsed = parse(String(value ?? fallback), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

export default function FilterBar({
  inputFields = [],
  defaultValues = {},
  onClear,
  onSubmit,
  showLabels = true,
  autoSubmitOnTabChange = false,
  isLoading = false,
}: ReportFiltersProps) {
  const tabFields = inputFields.filter(
    (field) => field.control_type === "tabs",
  );
  const nonTabFields = inputFields.filter(
    (field) => field.control_type !== "tabs",
  );

  const [formValues, setFormValues] =
    useState<Record<string, string | number>>(defaultValues);
  const [dateValues, setDateValues] = useState<Record<string, Date | null>>({});
  const [searchSelections, setSearchSelections] = useState<
    Record<string, SelectOption | null>
  >({});
  const [filterError, setFilterError] = useState<string | null>(null);
  const startDateRef = useRef<ReactDatePicker>(null);
  const endDateRef = useRef<ReactDatePicker>(null);

  const closeDatePickers = () => {
    startDateRef.current?.setOpen(false);
    endDateRef.current?.setOpen(false);
  };

  const isInsuranceFilterKey = (key: string) => {
    const normalizedKey = key.toLowerCase();
    return (
      normalizedKey.includes("insurance") ||
      normalizedKey.includes("isurance") ||
      normalizedKey.includes("isurnace")
    );
  };

  useEffect(() => {
    const nextInitialValues = getInitialValues(inputFields);
    const searchableFieldKeys = new Set(
      inputFields
        .filter((field) => field.control_type === "search")
        .map((field) => field.key),
    );

    setFormValues((previousValues) => {
      if (Object.keys(defaultValues).length > 0) {
        return {
          ...nextInitialValues,
          ...defaultValues,
        };
      }

      return {
        ...nextInitialValues,
        ...previousValues,
      };
    });

    setDateValues({
      start_date: toValidDate(
        defaultValues.start_date,
        String(nextInitialValues.start_date ?? START_DATE),
      ),
      end_date: toValidDate(
        defaultValues.end_date,
        String(nextInitialValues.end_date ?? END_DATE),
      ),
    });
    setFilterError(null);
    setSearchSelections((previousSelections) => {
      const nextSelections: Record<string, SelectOption | null> = {};

      Object.keys(previousSelections).forEach((key) => {
        if (searchableFieldKeys.has(key)) {
          const selectedOption = previousSelections[key];
          if (selectedOption !== undefined) {
            nextSelections[key] = selectedOption;
          }
        }
      });

      return nextSelections;
    });
  }, [inputFields, defaultValues]);

  const handleInputChange = (name: string, value: string | number) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleTabChange = (name: string, value: string | number) => {
    const nextValues: Record<string, string | number> = {
      ...formValues,
      [name]: value,
    };

    setFormValues(nextValues);

    if (autoSubmitOnTabChange) {
      onSubmit?.({ fields: nextValues });
    }
  };

  const handleDateChange = (key: string, value: Date | null) => {
    setDateValues((prev) => ({ ...prev, [key]: value }));
    setFormValues((prev) => {
      const nextValues = {
        ...prev,
        [key]: value ? format(value, "yyyy-MM-dd") : "",
      };

      if (nextValues.start_date && nextValues.end_date) {
        if (new Date(nextValues.start_date) > new Date(nextValues.end_date)) {
          setFilterError("End Date cannot be earlier than Start Date.");
        } else {
          setFilterError(null);
        }
      } else {
        setFilterError(null);
      }

      return nextValues;
    });
  };

  const handleSearchChange = (key: string, option: SelectOption | null) => {
    setSearchSelections((prev) => ({ ...prev, [key]: option }));
    setFormValues((prev) => ({
      ...prev,
      [key]:
        option == null
          ? ""
          : isInsuranceFilterKey(key)
            ? String(option.label)
            : String(option.value),
    }));
  };

  const getFilteredData = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (filterError) return;

    if (formValues.start_date && formValues.end_date) {
      if (new Date(formValues.start_date) > new Date(formValues.end_date)) {
        setFilterError("End Date cannot be earlier than Start Date.");
        return;
      }
    }
    setFilterError(null);
    onSubmit?.({
      fields: formValues,
    });
  };

  const resetFilterData = () => {
    const nextInitialValues = getInitialValues(inputFields);

    setFormValues(nextInitialValues);
    setDateValues({
      start_date: toValidDate(nextInitialValues.start_date, String(START_DATE)),
      end_date: toValidDate(nextInitialValues.end_date, String(END_DATE)),
    });
    setSearchSelections({});
    setFilterError(null);
    onClear?.();
  };

  return (
    <form id="filter-form" onSubmit={getFilteredData}>
      {filterError && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          {filterError}
        </div>
      )}
      {tabFields.length > 0 && (
        <div className="mb-3 d-flex flex-column gap-2">
          {tabFields.map((inputProps) => (
            <Tabs
              key={inputProps.key}
              options={inputProps.static_options ?? []}
              value={
                formValues[inputProps.key] ?? defaultValues[inputProps.key]
              }
              onChange={(value) => handleTabChange(inputProps.key, value)}
            />
          ))}
        </div>
      )}
      <div id="filter-form-fields" className="row g-2 statistics-filter">
        {nonTabFields.length > 0 ? (
          nonTabFields.map((inputProps) => {
            if (inputProps.control_type === "search") {
              return inputProps.api_endpoint ? (
                <div className="col filter-col" key={inputProps.key}>
                  <SelectWithSearch
                    key={inputProps.key}
                    endpoint={inputProps.api_endpoint}
                    value={searchSelections[inputProps.key] ?? null}
                    onChange={(option) =>
                      handleSearchChange(inputProps.key, option)
                    }
                    placeholder={inputProps.label ?? "Search..."}
                  />
                </div>
              ) : null;
            }

            if (
              inputProps.control_type === "select" &&
              inputProps.key === "drug_name"
            ) {
              return (
                <div className="col filter-col mb-1" key={inputProps.key}>
                  {showLabels && (
                    <label className="form-label text-muted">Products</label>
                  )}
                  <Select
                    key={inputProps.key}
                    id={inputProps.key}
                    name={inputProps.key}
                    formValues={formValues}
                    defaultValues={defaultValues}
                    api_endpoint={inputProps.api_endpoint}
                    onChange={(value) =>
                      !Array.isArray(value)
                        ? handleInputChange(inputProps.key, value)
                        : undefined
                    }
                    onMenuOpen={closeDatePickers}
                    placeholder={inputProps.label ?? "inputProps.key"}
                    static_options={
                      inputProps.static_options ? inputProps.static_options : []
                    }
                  />
                </div>
              );
            }

            if (
              inputProps.control_type === "select" &&
              inputProps.key === "period"
            ) {
              return (
                <div className="col filter-col mb-1" key={inputProps.key}>
                  {showLabels && (
                    <label className="form-label text-muted">Group By</label>
                  )}
                  <Select
                    key={inputProps.key}
                    id={inputProps.key}
                    name={inputProps.key}
                    formValues={formValues}
                    defaultValues={defaultValues}
                    api_endpoint={inputProps.api_endpoint}
                    onChange={(value) =>
                      !Array.isArray(value)
                        ? handleInputChange(inputProps.key, value)
                        : undefined
                    }
                    onMenuOpen={closeDatePickers}
                    placeholder={inputProps.label ?? "inputProps.key"}
                    static_options={
                      inputProps.static_options ? inputProps.static_options : []
                    }
                  />
                </div>
              );
            }

            // if (
            //   inputProps.control_type === "select" &&
            //   inputProps.key === "filter_type"
            // ) {
            //   return (
            //     <div className="col filter-col mb-1" key={inputProps.key}>
            //       {showLabels && (
            //         <label className="form-label text-muted">
            //           Compare With
            //         </label>
            //       )}
            //       <Select
            //         key={inputProps.key}
            //         id={inputProps.key}
            //         name={inputProps.key}
            //         formValues={formValues}
            //         defaultValues={defaultValues}
            //         api_endpoint={inputProps.api_endpoint}
            //         onChange={(value) =>
            //           !Array.isArray(value)
            //             ? handleInputChange(inputProps.key, value)
            //             : undefined
            //         }
            //         onMenuOpen={closeDatePickers}
            //         placeholder={inputProps.label ?? "inputProps.key"}
            //         static_options={
            //           inputProps.static_options ? inputProps.static_options : []
            //         }
            //       />
            //     </div>
            //   );
            // }

            if (
              inputProps.control_type === "date" &&
              inputProps.key === "start_date"
            ) {
              return (
                <div className="col date-col mb-1" key={inputProps.key}>
                  {showLabels && (
                    <label className="form-label text-muted">Start Date</label>
                  )}
                  <DatePicker
                    ref={startDateRef}
                    key={inputProps.key}
                    id={inputProps.key}
                    name={inputProps.key}
                    value={dateValues[inputProps.key] ?? null}
                    onChange={(value) =>
                      handleDateChange(inputProps.key, value)
                    }
                    placeholder={inputProps.placeholder ?? "MM/dd/yyyy"}
                  />
                </div>
              );
            }
            if (
              inputProps.control_type === "date" &&
              inputProps.key === "end_date"
            ) {
              return (
                <div className="col date-col mb-1" key={inputProps.key}>
                  {showLabels && (
                    <label className="form-label text-muted">End Date</label>
                  )}
                  <DatePicker
                    ref={endDateRef}
                    key={inputProps.key}
                    id={inputProps.key}
                    name={inputProps.key}
                    value={dateValues[inputProps.key] ?? null}
                    onChange={(value) =>
                      handleDateChange(inputProps.key, value)
                    }
                    placeholder={inputProps.placeholder ?? "MM/dd/yyyy"}
                  />
                </div>
              );
            }

            return (
              <div
                className={
                  inputProps.control_type === "hidden"
                    ? "d-none"
                    : "col filter-col"
                }
                key={inputProps.key}
              >
                <Input
                  key={inputProps.key}
                  id={inputProps.key}
                  name={inputProps.key}
                  type={inputProps.control_type}
                  placeholder={inputProps.label ?? undefined}
                  value={formValues[inputProps.key] ?? ""}
                  onChange={(value) => handleInputChange(inputProps.key, value)}
                />
              </div>
            );
          })
        ) : inputFields.length === 0 ? (
          <div className="placeholder-glow col-12 col-sm-12 col-md-9 col-lg-10">
            <span
              className="placeholder w-100"
              style={{ height: "38px" }}
            ></span>
          </div>
        ) : null}

        <div className="col btn-col">
          <div className="row g-2">
            <div className="col-6 col-sm-6">
              <button type="submit" className="btn btn-primary w-100">
                <i className="bi bi-funnel-fill small"></i> Filter
              </button>
            </div>
            <div className="col-6 col-sm-6">
              <button
                type="button"
                onClick={resetFilterData}
                className="btn btn-light w-100"
              >
                <i className="bi bi-eraser-fill"></i> Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
